// ─── Brand Colour Helpers ──────────────────────────────────────────────────────
// Pure functions with no DOM or React dependency so they stay directly testable.
//
// The app's colour tokens are stored as space-separated RGB channel triplets
// (e.g. `--amber: 232 163 61`) because Tailwind composes them as
// `rgb(var(--amber) / <alpha-value>)`. Every value produced here uses that same
// triplet form so an Operator accent can be substituted without touching any
// Tailwind class name.

// Matches the backend's own `^#[0-9A-Fa-f]{6}$` branding validator exactly.
// Shorthand (#abc), alpha (#aabbccdd), rgb()/hsl() functions and named colours
// are all rejected, so a malformed or hostile value can never reach the DOM.
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

// Readability bounds, anchored to measured contrast against the app's own
// backgrounds rather than arbitrary numbers.
//
// Dark theme: `--bg-base` #070A0F has a relative luminance of ~0.005, so an
// accent at 0.21 clears WCAG AA for normal text (~4.7:1). The stock dark amber
// sits at ~0.44, well above the bound, so it is never adjusted.
//
// Light theme: accents are drawn on white `--bg-panel`. The stock light amber
// #C77E17 measures ~0.27, so that is used as the ceiling — an Operator colour is
// held to the same readability the product already ships, and no stricter.
const DARK_THEME_MIN_LUMINANCE = 0.21;
const LIGHT_THEME_MAX_LUMINANCE = 0.28;

export function isValidHexColor(value) {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

export function hexToRgb(value) {
  if (!isValidHexColor(value)) return null;
  return {
    r: parseInt(value.slice(1, 3), 16),
    g: parseInt(value.slice(3, 5), 16),
    b: parseInt(value.slice(5, 7), 16),
  };
}

export function rgbToTriplet(rgb) {
  if (!rgb) return null;
  return `${clampChannel(rgb.r)} ${clampChannel(rgb.g)} ${clampChannel(rgb.b)}`;
}

export function hexToRgbTriplet(value) {
  return rgbToTriplet(hexToRgb(value));
}

function clampChannel(channel) {
  return Math.max(0, Math.min(255, Math.round(channel)));
}

// WCAG relative luminance, used both for picking readable text on the accent and
// for deciding whether the accent itself needs adjusting for the active theme.
export function relativeLuminance(rgb) {
  if (!rgb) return 0;
  const channel = (raw) => {
    const c = clampChannel(raw) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

// Text/icon colour placed directly on top of the accent. Mirrors the existing
// `--amber-text-on` token's role.
export function contrastTextTriplet(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  // The existing dark-on-amber token (23 19 10) is reused rather than pure black
  // so a light Operator accent keeps the same visual weight as the default brand.
  return relativeLuminance(rgb) > 0.45 ? '23 19 10' : '255 255 255';
}

function mix(rgb, target, ratio) {
  return {
    r: rgb.r + (target - rgb.r) * ratio,
    g: rgb.g + (target - rgb.g) * ratio,
    b: rgb.b + (target - rgb.b) * ratio,
  };
}

// An Operator picks one colour, but it has to stay legible on both the near-black
// dark theme and the light blue-grey light theme. Lighten an over-dark accent for
// dark mode and darken an over-bright accent for light mode; leave anything
// already in range untouched so the Operator's chosen colour is preserved.
export function readableAccentRgb(hex, theme) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const luminance = relativeLuminance(rgb);
  if (theme === 'light') {
    if (luminance <= LIGHT_THEME_MAX_LUMINANCE) return rgb;
    const excess = Math.min(1, (luminance - LIGHT_THEME_MAX_LUMINANCE) / (1 - LIGHT_THEME_MAX_LUMINANCE));
    return mix(rgb, 0, 0.2 + 0.45 * excess);
  }
  if (luminance >= DARK_THEME_MIN_LUMINANCE) return rgb;
  const deficit = Math.min(1, (DARK_THEME_MIN_LUMINANCE - luminance) / DARK_THEME_MIN_LUMINANCE);
  return mix(rgb, 255, 0.2 + 0.45 * deficit);
}

export function readableAccentTriplet(hex, theme) {
  return rgbToTriplet(readableAccentRgb(hex, theme));
}

// Hover state: nudge toward white on dark, toward black on light, matching how
// `--amber-hover` relates to `--amber` in each theme.
export function accentHoverTriplet(hex, theme) {
  const rgb = readableAccentRgb(hex, theme);
  if (!rgb) return null;
  return rgbToTriplet(theme === 'light' ? mix(rgb, 0, 0.12) : mix(rgb, 255, 0.12));
}
